import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Modal, Button, Spinner } from '../ui';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { Upload, FileUp, Download, CheckCircle, AlertCircle, X } from 'lucide-react';
import api from '@/config/axios';

const BulkInvestorImportModal = ({ isOpen, onClose, onSuccess }) => {
  const { error: showError, success: showSuccess } = useToastContext();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('upload'); // upload, preview, processing
  const [uploadError, setUploadError] = useState(null);
  const [importResults, setImportResults] = useState(null);

  const normalizeDate = (value) => {
    if (!value) return '';
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    const asDate = new Date(value);
    if (!Number.isNaN(asDate.getTime())) {
      const y = asDate.getFullYear();
      const m = String(asDate.getMonth() + 1).padStart(2, '0');
      const d = String(asDate.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return String(value);
  };

  const parseFullName = (fullName = '') => {
    const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return { firstName: '', lastName: '' };
    }
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ') || parts[0],
    };
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (
      selectedFile.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
      selectedFile.type !== 'application/vnd.ms-excel'
    ) {
      showError('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setFile(selectedFile);
    parseFile(selectedFile);
  };

  const parseFile = (targetFile) => {
    setLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const bytes = new Uint8Array(e.target.result);
        const workbook = XLSX.read(bytes, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '', cellDates: true });

        if (jsonData.length === 0) {
          setUploadError('The uploaded file is empty.');
          setFile(null);
          return;
        }

        const processedData = jsonData.map((row, index) => {
          const fullName = row['FULL NAME'] || row.fullName || row.name || '';
          const parsedName = parseFullName(fullName);

          return {
            _id: index,
            firstName: row['FIRST NAME'] || row.firstName || parsedName.firstName,
            lastName: row['LAST NAME'] || row.lastName || parsedName.lastName,
            email: row['EMAIL'] || row.email || '',
            password: row['PASSWORD'] || row.password || '',
            invested_amount: row['INVESTED AMOUNT (USDT)'] || row.invested_amount || 0,
            roi_min: row['ROI MIN (%)'] || row.roi_min || 0.5,
            roi_max: row['ROI MAX (%)'] || row.roi_max || 1.5,
            startDate: normalizeDate(row['START DATE'] || row.startDate),
            broker_email: row['BROKER EMAIL'] || row.broker_email || '',
            broker_min_roi: row['BROKER ROI MIN (%)'] || row.broker_min_roi || 0,
            broker_max_roi: row['BROKER ROI MAX (%)'] || row.broker_max_roi || 0,
          };
        });

        setData(processedData);
        setStep('preview');
        setUploadError(null);
      } catch (err) {
        console.error('Error parsing investor import file:', err);
        setUploadError('Failed to parse the file. Please ensure it is a valid template.');
        setFile(null);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(targetFile);
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        'FIRST NAME': 'Ali',
        'LAST NAME': 'Khan',
        EMAIL: 'ali.khan@example.com',
        PASSWORD: 'Investor123',
        'INVESTED AMOUNT (USDT)': 10000,
        'ROI MIN (%)': 0.5,
        'ROI MAX (%)': 1.5,
        'START DATE': '2026-03-01',
        'BROKER EMAIL': 'broker1@example.com',
        'BROKER ROI MIN (%)': 0.25,
        'BROKER ROI MAX (%)': 0.75,
      },
      {
        'FIRST NAME': 'Sara',
        'LAST NAME': 'Noor',
        EMAIL: 'sara.noor@example.com',
        PASSWORD: 'Investor123',
        'INVESTED AMOUNT (USDT)': 5000,
        'ROI MIN (%)': 0.5,
        'ROI MAX (%)': 1.5,
        'START DATE': '2026-03-05',
        'BROKER EMAIL': '',
        'BROKER ROI MIN (%)': 0,
        'BROKER ROI MAX (%)': 0,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Investors');
    XLSX.writeFile(workbook, 'investor_import_template.xlsx');
  };

  const handleCellChange = (id, field, value) => {
    setData((prev) => prev.map((row) => (row._id === id ? { ...row, [field]: value } : row)));
  };

  const handleRemoveRow = (id) => {
    setData((prev) => prev.filter((row) => row._id !== id));
  };

  const validateData = () => {
    const validRows = data.filter((row) => row.firstName && row.lastName && row.email && row.password);

    if (validRows.length === 0) {
      showError('No valid rows found to import.');
      return false;
    }

    if (validRows.length !== data.length) {
      showError(`${data.length - validRows.length} invalid rows found. Please fix or remove them.`);
      return false;
    }

    return true;
  };

  const handleImport = async () => {
    if (!validateData()) return;

    setLoading(true);
    setStep('processing');

    try {
      const investorsToImport = data.map(({ _id, ...rest }) => ({
        ...rest,
        invested_amount: rest.invested_amount ? parseFloat(rest.invested_amount) : 0,
        roi_min: rest.roi_min ? parseFloat(rest.roi_min) : 0,
        roi_max: rest.roi_max ? parseFloat(rest.roi_max) : 0,
        broker_min_roi: rest.broker_min_roi ? parseFloat(rest.broker_min_roi) : 0,
        broker_max_roi: rest.broker_max_roi ? parseFloat(rest.broker_max_roi) : 0,
      }));

      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < investorsToImport.length; i += chunkSize) {
        chunks.push(investorsToImport.slice(i, i + chunkSize));
      }

      let totalProcessed = 0;
      let totalFailed = 0;
      const allErrors = [];

      for (const chunk of chunks) {
        const response = await api.post('/api/users/bulk-import-investors', { investors: chunk });
        if (response.data.success) {
          totalProcessed += response.data.processed || 0;
          totalFailed += response.data.failed || 0;
          if (response.data.errors) {
            allErrors.push(...response.data.errors);
          }
        }
      }

      setImportResults({
        processed: totalProcessed,
        failed: totalFailed,
        errors: allErrors,
      });

      if (totalProcessed > 0) {
        showSuccess(`Successfully imported ${totalProcessed} investors.`);
        if (onSuccess) onSuccess();
      }

      if (totalFailed > 0) {
        showError(`Failed to import ${totalFailed} investors.`);
      }
    } catch (error) {
      console.error('Investor bulk import error:', error);
      setUploadError(error.response?.data?.message || 'Failed to import investors');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setData([]);
    setStep('upload');
    setUploadError(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderUploadStep = () => (
    <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-primary-200 dark:border-primary-700 rounded-lg bg-primary-50 dark:bg-primary-900/30">
      <FileUp size={48} className="text-primary-400 mb-4" />
      <h3 className="text-lg font-medium text-primary-900 dark:text-primary-50 mb-2">Upload Investor File</h3>
      <p className="text-sm text-primary-600 dark:text-primary-400 mb-6 text-center">
        Upload investor data in Excel format.
        <br />
        Supported formats: .xlsx, .xls
      </p>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".xlsx, .xls"
        className="hidden"
      />

      <div className="flex gap-4">
        <Button variant="outline" onClick={downloadTemplate} leftIcon={<Download size={16} />}>
          Download Template
        </Button>
        <Button onClick={() => fileInputRef.current?.click()} leftIcon={<Upload size={16} />}>
          Select File
        </Button>
      </div>

      {uploadError && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md flex items-center gap-2 text-sm">
          <AlertCircle size={16} />
          {uploadError}
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-primary-600 dark:text-primary-400">Previewing {data.length} investors</div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={handleReset} size="sm">
            Cancel
          </Button>
          <Button onClick={handleImport} size="sm" isLoading={loading}>
            Confirm Import
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto border border-primary-200 dark:border-primary-700 rounded-lg">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-primary-700 uppercase bg-primary-50 dark:bg-primary-800 dark:text-primary-400 sticky top-0">
            <tr>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">First Name</th>
              <th className="px-3 py-2">Last Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Password</th>
              <th className="px-3 py-2">Invested</th>
              <th className="px-3 py-2">ROI Min</th>
              <th className="px-3 py-2">ROI Max</th>
              <th className="px-3 py-2">Start Date</th>
              <th className="px-3 py-2">Broker Email</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row._id} className="bg-white border-b dark:bg-primary-900 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-800/50">
                <td className="px-3 py-2">
                  <button onClick={() => handleRemoveRow(row._id)} className="text-red-500 hover:text-red-700">
                    <X size={14} />
                  </button>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.firstName || ''}
                    onChange={(e) => handleCellChange(row._id, 'firstName', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-28"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.lastName || ''}
                    onChange={(e) => handleCellChange(row._id, 'lastName', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-28"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="email"
                    value={row.email || ''}
                    onChange={(e) => handleCellChange(row._id, 'email', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-48"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.password || ''}
                    onChange={(e) => handleCellChange(row._id, 'password', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-32"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={row.invested_amount || 0}
                    onChange={(e) => handleCellChange(row._id, 'invested_amount', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-24"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={row.roi_min || 0}
                    onChange={(e) => handleCellChange(row._id, 'roi_min', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-16"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={row.roi_max || 0}
                    onChange={(e) => handleCellChange(row._id, 'roi_max', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-16"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.startDate || ''}
                    onChange={(e) => handleCellChange(row._id, 'startDate', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-28"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.broker_email || ''}
                    onChange={(e) => handleCellChange(row._id, 'broker_email', e.target.value)}
                    className="bg-transparent border-none focus:ring-0 p-0 text-xs w-40"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="flex flex-col items-center justify-center py-12">
      {importResults ? (
        <>
          {importResults.failed === 0 ? (
            <CheckCircle size={48} className="text-green-500 mb-4" />
          ) : (
            <AlertCircle size={48} className="text-yellow-500 mb-4" />
          )}

          <h3 className="text-xl font-medium text-primary-900 dark:text-primary-50 mb-2">Import Complete</h3>
          <p className="text-primary-600 dark:text-primary-400 mb-6 text-center">
            Processed: {importResults.processed}
            <br />
            Failed: {importResults.failed}
          </p>

          {importResults.errors && importResults.errors.length > 0 && (
            <div className="w-full max-h-40 overflow-auto bg-red-50 dark:bg-red-900/20 p-3 rounded-lg mb-6 text-xs code">
              {importResults.errors.map((err, idx) => (
                <div key={idx} className="text-red-600 dark:text-red-400 border-b border-red-100 dark:border-red-800 last:border-0 py-1">
                  Row {(err.index || 0) + 1}: {err.message}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-4">
            <Button onClick={handleReset} variant="outline">
              Import More
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </>
      ) : (
        <>
          <Spinner size="xl" className="mb-4" />
          <h3 className="text-lg font-medium text-primary-900 dark:text-primary-50">Importing investors...</h3>
          <p className="text-sm text-primary-600 dark:text-primary-400 mt-2">This may take a moment for historical ROI generation.</p>
        </>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Import Investors"
      size="xl"
      closeOnOverlayClick={false}
    >
      <div className="p-1">
        {step === 'upload' && renderUploadStep()}
        {step === 'preview' && renderPreviewStep()}
        {step === 'processing' && renderProcessingStep()}
      </div>
    </Modal>
  );
};

export default BulkInvestorImportModal;
